import { useState, useRef } from "react";
import axios from "axios";
import { Upload, Trash2, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Client-side compression: downsize to MAX_DIM on the longest edge, output JPEG
// at 0.82 quality. Keeps GIFs untouched to preserve animation.
const MAX_DIM = 1920;
const JPEG_QUALITY = 0.82;

const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file || file.type === "image/gif") return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const { width: w, height: h } = img;
      const scale = Math.min(1, MAX_DIM / Math.max(w, h));
      // If image already small and under 500 KB, skip re-encoding
      if (scale === 1 && file.size < 500 * 1024) {
        URL.revokeObjectURL(url);
        return resolve(file);
      }
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return resolve(file);
          // If compression didn't help, keep original
          if (blob.size >= file.size) return resolve(file);
          const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
          resolve(new File([blob], name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

/**
 * UploadBox: image upload tile for the admin. Uploads to /api/admin/upload with
 * the given slot, shows a live preview, and allows removing the uploaded image.
 */
export const UploadBox = ({ slot, label, adminKey, present, onChanged, aspect = "aspect-[4/3]" }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(present ? `${API}/images/${slot}?t=${Date.now()}` : null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setLoading(true);
    setProgress("Optimizando...");
    try {
      const optimized = await compressImage(file);
      setProgress("Subiendo...");
      const form = new FormData();
      form.append("key", adminKey);
      form.append("slot", slot);
      form.append("file", optimized);
      await axios.post(`${API}/admin/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(`${API}/images/${slot}?t=${Date.now()}`);
      onChanged && onChanged(slot, true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al subir");
    } finally {
      setLoading(false);
      setProgress("");
    }
  };

  const handleRemove = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/images/${slot}`, { params: { key: adminKey } });
      setPreview(null);
      onChanged && onChanged(slot, false);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`upload-box-${slot}`} className="bg-[#141414] border border-q5 overflow-hidden">
      <div className={`relative ${aspect} bg-gradient-to-br from-[#151515] to-[#0C0C0C] overflow-hidden`}>
        {preview ? (
          <img src={preview} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-neutral-600">
            <Upload size={22} className="text-gold/40 mb-1.5" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Sin foto</span>
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2">
            <Loader2 size={22} className="text-gold animate-spin" />
            {progress && <span className="text-[10px] uppercase tracking-[0.2em] text-gold/80">{progress}</span>}
          </div>
        )}
        {preview && !loading && (
          <span className="absolute top-2 right-2 bg-emerald-600 text-white rounded-full p-1"><Check size={12} /></span>
        )}
      </div>
      <div className="p-3">
        <p className="text-xs text-neutral-300 truncate mb-2" title={label}>{label}</p>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            data-testid={`upload-input-${slot}`}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            data-testid={`upload-btn-${slot}`}
            onClick={() => inputRef.current?.click()}
            className="flex-1 text-[10px] uppercase tracking-[0.15em] px-2 py-2 border border-gold/50 text-gold hover:bg-gold/10 transition-colors flex items-center justify-center gap-1.5"
          >
            <Upload size={11} /> {preview ? "Cambiar" : "Subir"}
          </button>
          {preview && (
            <button
              type="button"
              data-testid={`upload-remove-${slot}`}
              onClick={handleRemove}
              className="px-2 py-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
              aria-label="Quitar"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
        {error && <p className="text-red-400 text-[10px] mt-1.5">{error}</p>}
      </div>
    </div>
  );
};
